import React from "react"
import Reveal from "../Reveal.jsx"

// Promo-aware variant of the standard Pricing block. Shows the
// discounted price prominently with the regular price struck through;
// when the promo is closed, falls back to a near-identity render that
// matches the standard pricing page.
const INCLUDED = [
  "Everything ShopShift runs — same product, smaller price tag.",
  "30 days free. No card charge during the trial.",
  "50% off for three months once the trial ends — that's $49.50/mo instead of $99.",
  "Then standard $99/mo per webshop, no annual commitment.",
  "Cancel any time and you're never billed again.",
  "No CRO consultant, no setup project, no learning curve."
]

function formatDollars(cents) {
  return `$${(Math.round(cents) / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`
}

export default function LaunchPricing({ priceCents = 9900, closed = false }) {
  const fullPrice = formatDollars(priceCents)
  const promoPrice = formatDollars(priceCents / 2)

  return (
    <section
      id="pricing"
      className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-page)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-32">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 05 · What it costs
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            30 days free. Then 50% off for three months.
          </h2>
          <p className="mt-4 font-sans text-[15px] text-[color:var(--color-ink-secondary)] max-w-xl leading-[1.6]">
            Card required at sign-up — no charge during the trial. After 30 days, your first three months are $49.50 instead of $99. Then the standard $99/month from month four onwards, billed monthly in USD via Stripe.
          </p>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-px bg-[color:var(--color-rule)] border border-[color:var(--color-rule)] rounded-[var(--radius-card-lg)] overflow-hidden">
            <div className="bg-[color:var(--color-surface-card)] p-12 flex flex-col justify-between gap-10">
              <div>
                <span className="inline-flex items-center gap-2 bg-[color:var(--color-winner-fill)] border border-[color:var(--color-winner)]/20 rounded-[var(--radius-pill)] px-3 py-1.5 self-start mb-6">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-winner-dot)]" />
                  <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-[color:var(--color-winner)] font-medium">
                    {closed ? "Promo closed" : "Launch promo · 50% off · 3 months"}
                  </span>
                </span>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)] mb-4">
                  Months 1 – 3 · after the free trial
                </p>
                <div className="flex items-baseline gap-3">
                  <span
                    className="font-sans font-medium text-[color:var(--color-ink-primary)] leading-none"
                    style={{ fontSize: "84px", letterSpacing: "-0.045em", fontVariantNumeric: "tabular-nums" }}
                  >
                    {closed ? fullPrice : promoPrice}
                  </span>
                  <span className="font-sans text-[18px] text-[color:var(--color-ink-tertiary)] tracking-[-0.02em]">
                    /month
                  </span>
                  {!closed && (
                    <span className="font-sans text-[18px] text-[color:var(--color-ink-faint)] line-through tracking-[-0.02em]">
                      {fullPrice}
                    </span>
                  )}
                </div>
                <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-tertiary)]">
                  Then {fullPrice}/month from month 4 · cancel any time
                </p>
              </div>

              <a
                href="/users/sign_up"
                className="inline-flex items-center justify-center bg-[color:var(--color-brand)] text-white font-sans text-[14px] font-medium px-5 py-3.5 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity self-start"
              >
                {closed ? "Start your free trial" : "Claim your spot — start free"}
              </a>
            </div>

            <div className="bg-[color:var(--color-surface-card)] p-12">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)] mb-6">
                What you get
              </p>
              <ul className="flex flex-col gap-4">
                {INCLUDED.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 font-sans text-[14px] text-[color:var(--color-ink-primary)] leading-[1.55]"
                  >
                    <span
                      aria-hidden
                      className="mt-2 inline-block w-1 h-1 rounded-full bg-[color:var(--color-ai)] flex-shrink-0"
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={240}>
          <p className="mt-10 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
            One subscription · one webshop · the discount is honoured for the full three months once you sign up — even if the 100 spots fill before then.
          </p>
        </Reveal>
      </div>
    </section>
  )
}
